import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandHoldingHeart,
  faLeaf,
  faClock,
} from '@fortawesome/free-solid-svg-icons';

// Import components
import HomeHero from '../components/HomeHero';
import FeaturedFlowers from '../components/FeaturedFlowers';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const HomePage = () => {
  return (
    <motion.div
      className="mt-20 relative"
      initial="hidden"
      animate="visible"
      variants={fadeInUp}
      exit={{ opacity: 0 }}
    >
      {/* Hero Section */}
      <HomeHero />

      {/* Value Proposition Section */}
      {/* Featured Flowers Section */}
      <FeaturedFlowers />

      {/* Value Proposition Section */}
      <section
        className="bg-gradient-to-br from-light to-white py-xl relative"
        id="values"
      >
        <div
          className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+CjxyZWN0IHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjNmI3ZDk0IiBzdHJva2Utb3BhY2l0eT0iMC4wMiIgc3Ryb2tlLXdpZHRoPSIxIj48L3JlY3Q+Cjwvc3ZnPg==')]
          opacity-30 z-0 pointer-events-none"
        ></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-lg relative z-1">
            <motion.h2
              variants={fadeInUp}
              className="relative inline-block pb-sm font-bold tracking-wider uppercase"
            >
              <span className="block font-accent text-base font-normal text-secondary not-italic mb-2 tracking-wider">
                Experience Excellence
              </span>
              Why Choose Curators' Studio
              <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[100px] h-[1px] bg-gradient-to-r from-transparent via-secondary to-transparent"></span>
            </motion.h2>
          </div>

          <motion.div
            variants={staggerContainer}
            className="flex flex-col md:flex-row justify-between gap-md z-2 relative"
          >
            <motion.div
              variants={fadeInUp}
              className="flex-1 min-w-[280px] p-lg text-center bg-white shadow-pastel-sm hover:shadow-pastel-md transition-all duration-300 relative border border-secondary/5 rounded-soft hover:-translate-y-1"
            >
              <div className="absolute top-0 left-0 w-5 h-5 border-t border-l border-secondary opacity-50 transition-all duration-300"></div>
              <div className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-secondary opacity-50 transition-all duration-300"></div>

              <div className="text-4xl text-secondary mb-sm drop-shadow-sm transition-all duration-300">
                <FontAwesomeIcon icon={faHandHoldingHeart} />
              </div>
              <h3 className="mb-sm text-primary font-semibold relative pb-2.5 tracking-wider">
                Expert Curation
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[30px] h-[1px] bg-secondary"></span>
              </h3>
              <p className="text-text-light leading-relaxed text-sm tracking-tight">
                Handpicked by floral design experts with years of experience in
                luxury arrangements.
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="flex-1 min-w-[280px] p-lg text-center bg-white shadow-pastel-sm hover:shadow-pastel-md transition-all duration-300 relative border border-secondary/5 rounded-soft hover:-translate-y-1"
            >
              <div className="absolute top-0 left-0 w-5 h-5 border-t border-l border-secondary opacity-50 transition-all duration-300"></div>
              <div className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-secondary opacity-50 transition-all duration-300"></div>

              <div className="text-4xl text-secondary mb-sm drop-shadow-sm transition-all duration-300">
                <FontAwesomeIcon icon={faLeaf} />
              </div>
              <h3 className="mb-sm text-primary font-semibold relative pb-2.5 tracking-wider">
                Unmatched Freshness
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[30px] h-[1px] bg-secondary"></span>
              </h3>
              <p className="text-text-light leading-relaxed text-sm tracking-tight">
                From garden to your home within hours, ensuring the longest vase
                life possible.
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="flex-1 min-w-[280px] p-lg text-center bg-white shadow-pastel-sm hover:shadow-pastel-md transition-all duration-300 relative border border-secondary/5 rounded-soft hover:-translate-y-1"
            >
              <div className="absolute top-0 left-0 w-5 h-5 border-t border-l border-secondary opacity-50 transition-all duration-300"></div>
              <div className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-secondary opacity-50 transition-all duration-300"></div>

              <div className="text-4xl text-secondary mb-sm drop-shadow-sm transition-all duration-300">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <h3 className="mb-sm text-primary font-semibold relative pb-2.5 tracking-wider">
                Effortless Luxury
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[30px] h-[1px] bg-secondary"></span>
              </h3>
              <p className="text-text-light leading-relaxed text-sm tracking-tight">
                Doorstep delivery on your schedule, bringing beauty to your home
                without any effort.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section
        className="py-xl bg-gradient-to-br from-sky/5 to-white relative"
        id="features"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-lavender/10 to-white"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-lg relative z-1">
            <motion.h2
              variants={fadeInUp}
              className="relative inline-block pb-sm font-bold tracking-wider uppercase"
            >
              <span className="block font-accent text-base font-normal text-secondary not-italic mb-2 tracking-wider">
                Tailored for You
              </span>
              Our Premium Offerings
              <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[100px] h-[1px] bg-gradient-to-r from-transparent via-secondary to-transparent"></span>
            </motion.h2>
          </div>

          <motion.div
            variants={fadeInUp}
            className="flex flex-col lg:flex-row items-center mb-lg gap-lg relative z-2"
          >
            <div className="flex-1 p-md">
              <h3 className="text-3xl mb-sm text-primary tracking-wider relative pb-3 font-semibold">
                Flexible Delivery Options
                <span className="absolute bottom-0 left-0 w-10 h-[1px] bg-secondary"></span>
              </h3>
              <p className="mb-sm leading-relaxed text-lg text-text-light tracking-wide">
                Choose from once, twice, or thrice weekly deliveries to suit
                your lifestyle and preferences. Our flexible subscription model
                ensures you always have fresh flowers when you want them.
              </p>
              <ul className="mt-md">
                <li className="relative pl-10 mb-sm text-lg leading-relaxed text-text-dark before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-secondary before:rounded-full before:shadow-[0_0_0_4px_rgba(212,175,55,0.15)]">
                  Once a week: Perfect for weekend entertaining
                </li>
                <li className="relative pl-10 mb-sm text-lg leading-relaxed text-text-dark before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-secondary before:rounded-full before:shadow-[0_0_0_4px_rgba(212,175,55,0.15)]">
                  Twice a week: Keep your home consistently refreshed
                </li>
                <li className="relative pl-10 mb-sm text-lg leading-relaxed text-text-dark before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-secondary before:rounded-full before:shadow-[0_0_0_4px_rgba(212,175,55,0.15)]">
                  Thrice a week: The ultimate luxury experience
                </li>
              </ul>
            </div>
            <div className="flex-1 p-md relative">
              <div className="w-full h-[400px] bg-light relative overflow-hidden shadow-pastel-md transition-all duration-300 hover:scale-[1.01] hover:shadow-pastel-lg rounded-soft">
                <div className="absolute inset-0 shadow-[inset_0_0_0_1px_rgba(240,201,135,0.1)] z-2"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-blush/20 to-lavender/20 z-1"></div>
                <img
                  src="/assets/flowers/lily/pink/DSC02776-01_1800x1800.webp"
                  alt="Flower delivery"
                  className="absolute inset-0 w-full h-full object-cover opacity-80 z-0"
                />
              </div>
              <div className="absolute w-[120px] h-[120px] bottom-[-15px] right-0 border border-secondary opacity-20 -z-1"></div>
            </div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="flex flex-col lg:flex-row-reverse items-center mb-lg gap-lg relative z-2"
          >
            <div className="flex-1 p-md">
              <h3 className="text-3xl mb-sm text-primary tracking-wider relative pb-3 font-semibold">
                Curated Collections
                <span className="absolute bottom-0 left-0 w-10 h-[1px] bg-secondary"></span>
              </h3>
              <p className="mb-sm leading-relaxed text-lg text-text-light tracking-wide">
                Each delivery is thoughtfully designed by our expert florists,
                considering seasonality, color harmony, and the latest trends in
                luxury home decor.
              </p>
              <p className="mb-sm leading-relaxed text-lg text-text-light tracking-wide">
                Our arrangements are created to complement sophisticated
                interiors and make a statement in any space.
              </p>
            </div>
            <div className="flex-1 p-md relative">
              <div className="w-full h-[400px] bg-light relative overflow-hidden shadow-pastel-md transition-all duration-300 hover:scale-[1.01] hover:shadow-pastel-lg rounded-soft">
                <div className="absolute inset-0 shadow-[inset_0_0_0_1px_rgba(240,201,135,0.1)] z-2"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-sky/20 to-mint/20 z-1"></div>
                <img
                  src="/assets/flowers/lily/white/white-lily.webp"
                  alt="Curated collections"
                  className="absolute inset-0 w-full h-full object-cover opacity-80 z-0"
                />
              </div>
              <div className="absolute w-[120px] h-[120px] top-[-15px] left-0 border border-secondary opacity-20 -z-1"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section
        className="bg-gradient-to-br from-primary/90 to-primary text-white py-xl relative overflow-hidden"
        id="about"
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+CjxyZWN0IHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0ibm9uZSI+PC9yZWN0Pgo8Y2lyY2xlIGN4PSIxIiBjeT0iMSIgcj0iMSIgZmlsbD0iI2YwYzk4NyIgZmlsbC1vcGFjaXR5PSIwLjA1Ij48L2NpcmNsZT4KPC9zdmc+')] opacity-40 z-1"></div>

        <div className="container mx-auto px-4 flex flex-col lg:flex-row items-center gap-lg relative z-2">
          <motion.div variants={fadeInUp} className="flex-[1.5]">
            <div className="text-center mb-lg relative z-1">
              <h2 className="relative inline-block pb-sm font-bold tracking-wider uppercase text-white">
                <span className="block font-accent text-base font-normal text-secondary not-italic mb-2 tracking-wider">
                  Our Story
                </span>
                Curators' Studio
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[100px] h-[1px] bg-gradient-to-r from-transparent via-secondary to-transparent"></span>
              </h2>
            </div>

            <p className="text-xl font-normal leading-relaxed mb-md tracking-wide text-light italic font-accent">
              We believe that luxury is in the details, and that every home
              deserves to be adorned with beauty.
            </p>

            <p className="leading-loose mb-md tracking-wide">
              Founded with a passion for bringing curated luxury into homes, The
              Curators' Studio specializes in premium flower subscriptions that
              transform living spaces into sophisticated sanctuaries.
            </p>

            <p className="leading-loose mb-md tracking-wide">
              Our team of expert florists and designers work tirelessly to
              source the finest blooms and create arrangements that reflect
              elegance, style, and contemporary luxury.
            </p>

            <p className="leading-loose mb-md tracking-wide">
              Based in Gurugram, we're committed to elevating the everyday
              experience of our discerning clients through the timeless beauty
              of fresh flowers.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="flex-1 relative">
            <div className="relative">
              <div className="absolute top-[-10px] left-[-10px] w-[calc(100%+20px)] h-[calc(100%+20px)] border border-secondary opacity-30 z-1"></div>
              <div className="w-full h-[350px] bg-light relative opacity-80 shadow-pastel-lg rounded-soft overflow-hidden">
                <img
                  src="/assets/flowers/lily/Lillies.webp"
                  alt="About Curators' Studio"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default HomePage;
